import type { TLearningContentDataWidget } from '@crehana/compass.dashlets';
import { formatDuration as formatDurationFromPackage } from '@crehana/date';

import { ContentType as GqlContentType } from '../../../../generated/graphql/types';

type TEdgeNode = NonNullable<
  NonNullable<
    NonNullable<
      import('./graphql/get-knowledgehub-content-by-collaborator.v5.generated').GetKnowledgehubContentByCollaboratorQuery['knowledge_hub']
    >['knowledge_hub_content_by_collaborator']
  >['edges'][number]['node']
>;

// Formats duration from seconds to "Xh Xm" forma
export const formatDuration = (duration?: number | null): string => {
  if (!duration) {
    return '';
  }

  return formatDurationFromPackage(duration);
};

// Maps course level names to difficulty levels from backend
export const mapDifficulty = (
  levelName?: string | null,
): 'beginner' | 'intermediate' | 'advanced' => {
  if (!levelName) {
    return 'beginner';
  }

  const name = levelName.toLowerCase();

  if (name.includes('avanzado') || name.includes('advanced')) {
    return 'advanced';
  }

  if (name.includes('intermedio') || name.includes('intermediate')) {
    return 'intermediate';
  }

  return 'beginner';
};

// Maps GraphQL content types to widget content types with fallback
export const mapContentType = (
  gqlContentType: GqlContentType,
): 'course' | 'workshop' | 'track' => {
  switch (gqlContentType) {
    case GqlContentType.ContentTypeCourse:
    case GqlContentType.ContentTypeElevateCourse:
    case GqlContentType.ContentTypeThirdPartyCourse: {
      return 'course';
    }
    case GqlContentType.ContentTypeTrack: {
      return 'track';
    }
    case GqlContentType.ContentTypePath: {
      return 'workshop';
    }
    default: {
      console.warn(
        `Unknown content type: ${gqlContentType}, defaulting to 'course'`,
      );
      return 'course';
    }
  }
};

// Transforms GraphQL nodes to widget-compatible data format
export const transformToWidgetData = (
  nodes: TEdgeNode[],
  contentType: GqlContentType,
): TLearningContentDataWidget[] =>
  nodes
    .filter(node => !!node?.course)
    .map((node, index) => {
      const course = node.course;
      const enrollmentUrl = node.enrollment_url ?? '';
      const numericId = Number.parseInt(String(node.id), 10);

      return {
        id: Number.isNaN(numericId) ? index : numericId,
        title: node.title ?? '',
        description: '',
        imageUrl: course?.image ?? '',
        isOutstanding: false,
        categoryNames: [],
        authorName: '',
        authorImageUrl: '',
        contentUrl: enrollmentUrl || '#',
        contentType: mapContentType(contentType),
        duration: formatDuration(course?.duration),
        difficulty: mapDifficulty(course?.course_level?.name ?? null),
        isNew: true,
        progress: undefined,
      };
    });
